import React, { useEffect, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { operationsManagerApprove, operationsManagerReject } from "actions";

/**
 * Modal for approving or rejecting / returning appraisal progress
 * @param {Object} props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onHide - Function to close modal
 * @param {string} props.appraisalId - Appraisal progress ID
 * @param {string} props.actionType - 'approve' or 'reject'
 * @param {Function} props.onSuccess - Callback after successful action
 * @param {string} props.objectViewedId - Object viewed ID for authorization
 */
const ApprovalModal = ({
  show,
  onHide,
  appraisalId,
  actionType,
  onSuccess,
  objectViewedId = null,
}) => {
  const dispatch = useDispatch();
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");

  const isReject = actionType === "reject";

  useEffect(() => {
    if (show) {
      setComments("");
      setValidationError("");
      setLoading(false);
    }
  }, [show, actionType]);

  const handleSubmit = async () => {
    if (!appraisalId) {
      return;
    }

    const trimmed = comments.trim();
    if (isReject && !trimmed) {
      setValidationError("A rejection reason is required.");
      return;
    }

    setLoading(true);
    setValidationError("");

    try {
      let result;

      // Backend determines current approval level and allowed role; frontend only triggers action.
      if (actionType === "approve") {
        result = await dispatch(operationsManagerApprove(appraisalId, trimmed, objectViewedId));
      } else if (isReject) {
        result = await dispatch(operationsManagerReject(appraisalId, trimmed, objectViewedId));
      }

      if (result) {
        setComments("");
        if (onSuccess) {
          onSuccess(result);
        }
        onHide();
      }
    } catch (error) {
      console.error("Error in approval action:", error);
      // Error is already handled by Redux action
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setComments("");
      setValidationError("");
      onHide();
    }
  };

  const title = isReject ? "Reject PAF / Return to Supervisor" : "Approve Appraisal";
  const confirmLabel = isReject ? "Reject & Return" : "Approve";
  const buttonClass = isReject ? "delete-btn" : "n-u-btn";

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="font-16 font-weight-bold heading-color">
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="label-input">
            {isReject ? "Reason for Rejection" : "Comments"}{" "}
            {isReject && <span className="text-danger">*</span>}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            className="input-field"
            placeholder={
              isReject
                ? "Please explain why this PAF is being returned to the supervisor."
                : "Enter comments for approve..."
            }
            value={comments}
            onChange={(e) => {
              setComments(e.target.value);
              if (validationError) setValidationError("");
            }}
            disabled={loading}
            required={isReject}
          />
          {isReject && (
            <Form.Text className={validationError ? "text-danger" : "text-muted"}>
              {validationError || "A reason is required when returning a PAF to the supervisor."}
            </Form.Text>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="c-b-btn"
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          className={buttonClass}
          onClick={handleSubmit}
          disabled={loading || (isReject && !comments.trim())}
        >
          {loading ? "Processing..." : confirmLabel}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApprovalModal;
